import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class UtilsService {
  /**
   * Checks if an object is empty.
   *
   * @param objectName - The object to check.
   * @returns True if the object is empty, false otherwise.
   */
  public isObjectEmpty = (objectName: any) => {
    return objectName && Object.keys(objectName).length === 0 && objectName.constructor === Object;
  };

  /**
   * Checks if a given key exists in an object.
   *
   * @param objectName - The object to check.
   * @param key - The key to search for.
   * @returns A boolean indicating whether the key exists in the object.
   */
  public isKeyInObject = (objectName: any, key: string) => {
    if (this.isObjectEmpty(objectName) === false) {
      return Object.keys(objectName).includes(key);
    }

    return false;
  };

  /**
   * Checks if an array is empty.
   *
   * @param arrayName - The array to check.
   * @returns True if the array is empty, false otherwise.
   */
  public isArrayEmpty = (arrayName: any[]) => {
    return arrayName && arrayName.length === 0 && arrayName.constructor === Array;
  };

  /**
   * Checks if a key exists in an array.
   *
   * @param arrayName - The array to check.
   * @param key - The key to search for.
   * @returns True if the key exists in the array, false otherwise.
   */
  public isKeyInArray = (arrayName: any[], key: string) => {
    if (this.isArrayEmpty(arrayName) === false) {
      return arrayName.includes(key);
    }

    return false;
  };

  /**
   * Orders an array of objects by a specified key in ascending order.
   *
   * @param array - The array to be ordered.
   * @param key - The key to sort the array by.
   * @returns The ordered array.
   */
  public orderArrayBy = (array: any[], key: string, order: string = 'asc') => {
    array.sort((a: any, b: any) => {
      if (a[key].toUpperCase() < b[key].toUpperCase()) {
        return order === 'asc' ? -1 : 1;
      }
      if (a[key].toUpperCase() > b[key].toUpperCase()) {
        return order === 'asc' ? 1 : -1;
      }
      return 0;
    });

    return array;
  };

  /**
   * Concatenates two objects into a new object.
   *
   * @param queryParams - The first object to be concatenated.
   * @param concat - The second object to be concatenated.
   * @returns The concatenated object.
   */
  public concatTwoObjects = (queryParams: any, concat: any) => {
    return { ...queryParams, ...concat };
  };

  /**
   * Generates a random string of the specified length using the provided characters.
   * Ensures that each character in the result is unique.
   *
   * @param {number} num - The length of the random string to generate.
   * @param {string} [characters="ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789"] - The set of characters to use for generating the random string.
   * @returns {string} A random string of the specified length with unique characters.
   */
  public generateRandomString = (num: number, characters: string = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789') => {
    const charactersLength = characters.length;
    let result = '',
      ch;

    while (result.length < num) {
      ch = characters.charAt(Math.floor(Math.random() * charactersLength));

      if (!result.includes(ch)) {
        result += ch;
      }
    }

    return result;
  };
}
